import { type MiddlewareFunction, data, redirect } from "react-router-dom";
import { userApi, type UserRole } from "../redux/api/user.api";
import { setToken } from "../redux/slices/auth.slice";
import { store } from "../redux/store";
import { authService, getAppConfig } from "framework-react-core";
import { isAuthorized } from "../utilities/auth";

const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

/**
 * Creates a middleware function that authenticates the user by acquiring an access token.
 *
 * Attempts token acquisition in the following order:
 * 1. acquireToken — uses cached MSAL active account (fastest, no network)
 * 2. ssoSilent — picks up an existing AAD/CRM browser session without user interaction
 * 3. Throws 403 — falls back to the ErrorRoot UI with the Sign In button in the header
 *
 * @returns A middleware function that handles user authentication
 * @throws {Response} A 403 response with redirect information if authentication fails
 */
export function authenticate(): MiddlewareFunction {
  return async function authenticateUser(props) {
    let accessToken: string | null = null;

    if (!authService) {
      const { pathname, search } = new URL(props.request.url);
      throw data(
        {
          message: "Please sign in to access this page.",
          redirect: `${pathname}${search}`,
        },
        { status: 500 },
      );
    }

    try {
      const result = await authService.acquireToken();
      accessToken = result.accessToken;
    } catch {
      // No active MSAL account — fall through to ssoSilent
    }

    if (!accessToken) {
      try {
        const scopes = getAppConfig().getConfigValue<{ default: string[] }>(
          "FRAMEWORK_UI_AUTH_SCOPES",
        ).default;
        const result = await authService.msalClient.ssoSilent({ scopes });
        authService.msalClient.setActiveAccount(result.account);
        accessToken = result.accessToken;
      } catch {
        // No existing AAD/CRM browser session — fall through to 403
      }
    }

    if (!accessToken) {
      const { pathname, search } = new URL(props.request.url);
      throw data(
        {
          message: "Please sign in to access this page.",
          redirect: `${pathname}${search}`,
        },
        { status: 403 },
      );
    }

    store.dispatch(setToken({ token: accessToken }));
    sessionStorage.removeItem("sso_redirect_in_progress");
    const meResult = await store.dispatch(
      userApi.endpoints.getMe.initiate(undefined, { subscribe: false }),
    );

    if (meResult.error) {
      const status =
        "status" in meResult.error ? meResult.error.status : undefined;
      const code = status === 401 || status === 403 ? 403 : 503;
      throw redirect(`/service-unavailable?code=${code}`);
    }

    // After a successful login, honor any stored post-login redirect path.
    const storedRedirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (storedRedirect) {
      const { pathname, search } = new URL(props.request.url);
      if (storedRedirect !== `${pathname}${search}`) {
        throw redirect(storedRedirect);
      }
    }
  };
}

/**
 * Creates middleware that authorizes the current user against a required set of roles.
 *
 * @param roles - The list of roles permitted to access the route.
 * @returns A middleware function that validates role-based access for the request.
 * @throws {Response} Throws a 403 data response when the user lacks required access.
 */
export function authorize(roles: UserRole[]): MiddlewareFunction {
  return function checkRoles() {
    const { data: user } = userApi.endpoints.getMe.select()(store.getState());
    const hasAccess = isAuthorized(user?.data, roles);
    if (!hasAccess) {
      throw redirect("/service-unavailable?code=403");
    }
  };
}
